import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Spin,
  message,
  Typography,
  Alert,
  Descriptions,
  Divider
} from 'antd';
import {
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/admin/governanceService';
import { useInvoiceSettings } from '@config/governance/invoiceSettings';
import ListPageHeader from '@shared/ListPageHeader';

const { Text } = Typography;
const { Option } = Select;

const BillingProfile = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingProfile, setBillingProfile] = useState(null);

  // Backend-driven invoice capabilities (single source of truth for UI structure)
  const [invoiceCapabilities, setInvoiceCapabilities] = useState({
    hasInvoiceSettings: false,
    carrierTypeOptions: [],
    invoiceFields: [],
  });

  const carrierType = Form.useWatch('carrier_type', form) || 'none';
  const locale = i18n.language?.startsWith('zh') ? 'zh' : 'en';

  // Frontend config: ONLY for client-side validation (pattern matching, checksum)
  // Falls back to default (no-op) when countryCode is unknown — no false negatives
  const countryCode = billingProfile?.country_code;
  const invoiceSettings = useInvoiceSettings(countryCode, carrierType);

  // Compute visible fields entirely from backend data — zero hardcoded field names
  const visibleFields = useMemo(() => {
    return invoiceCapabilities.invoiceFields
      .filter(field => field.required_for?.includes(carrierType))
      .map(field => {
        const config = { ...field };

        // Apply dynamic overrides for current carrier type
        if (field.dynamic_label?.[carrierType]) {
          config.label = field.dynamic_label[carrierType][locale] || field.label;
          config.label_en = field.dynamic_label[carrierType].en || field.label_en;
        }
        if (field.dynamic_placeholder?.[carrierType]) {
          config.placeholder = field.dynamic_placeholder[carrierType][locale] || field.placeholder;
          config.placeholder_en = field.dynamic_placeholder[carrierType].en || field.placeholder_en;
        }
        if (field.dynamic_hint?.[carrierType]) {
          config.hint = field.dynamic_hint[carrierType][locale] || field.hint;
          config.hint_en = field.dynamic_hint[carrierType].en || field.hint_en;
        }

        return config;
      });
  }, [invoiceCapabilities.invoiceFields, carrierType, locale]);

  // Build validation rules: required from backend, pattern/checksum from frontend config
  const buildValidationRules = useCallback((field) => {
    const fieldLabel = locale === 'zh' ? field.label : field.label_en;
    const rules = [
      {
        required: true,
        message: t(`admin.ai_center.billing_profile.${field.name}_required`, {
          defaultValue: `${fieldLabel} ${t('common.validation.is_required', { defaultValue: 'is required' })}`
        })
      }
    ];

    // Supplement with client-side validation from frontend config (pattern, checksum)
    if (invoiceSettings.validateField) {
      rules.push({
        validator: async (_, value) => {
          if (!value) return;
          const result = invoiceSettings.validateField(field.name, value);
          if (result && !result.valid) {
            throw new Error(result.error);
          }
        }
      });
    }

    return rules;
  }, [locale, t, invoiceSettings]);

  // Sync API response to local state
  const updateFromResponse = useCallback((responseData) => {
    setBillingProfile(responseData.billing_profile);
    if (responseData.billing_profile) {
      form.setFieldsValue(responseData.billing_profile);
    }
    setInvoiceCapabilities({
      hasInvoiceSettings: responseData.has_invoice_settings || false,
      carrierTypeOptions: responseData.carrier_type_options || [],
      invoiceFields: responseData.invoice_fields || [],
    });
  }, [form]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await governanceService.getBillingProfile();

      if (response.success) {
        updateFromResponse(response.data);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch billing profile:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t, updateFromResponse]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const response = await governanceService.updateBillingProfile(values);

      if (response.success) {
        message.success(t('admin.ai_center.billing_profile.save_success'));
        updateFromResponse(response.data);
      } else {
        message.error(response.message || t('common.error.save_failed'));
      }
    } catch (error) {
      console.error('Failed to update billing profile:', error);
      message.error(t('common.error.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader items={['ai_center', 'ai_center.billing_profile']} basePath="/admin" />

      <div style={{ padding: '0 24px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={billingProfile}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title={t('admin.ai_center.billing_profile.basic_info')}>
              <Form.Item
                name="billing_name"
                label={t('admin.ai_center.billing_profile.billing_name')}
                rules={[{ required: true, message: t('admin.ai_center.billing_profile.billing_name_required') }]}
              >
                <Input placeholder={t('admin.ai_center.billing_profile.billing_name_placeholder')} />
              </Form.Item>

              <Form.Item
                name="billing_email"
                label={t('admin.ai_center.billing_profile.billing_email')}
                rules={[
                  { required: true, message: t('admin.ai_center.billing_profile.billing_email_required') },
                  { type: 'email', message: t('admin.ai_center.billing_profile.billing_email_invalid') }
                ]}
              >
                <Input placeholder={t('admin.ai_center.billing_profile.billing_email_placeholder')} />
              </Form.Item>
            </Card>

            {/* Backend-driven: show invoice section based on API capability flags */}
            {invoiceCapabilities.hasInvoiceSettings && invoiceCapabilities.carrierTypeOptions.length > 0 && (
              <Card title={t('admin.ai_center.billing_profile.invoice_settings')}>
                <Alert
                  message={t('admin.ai_center.billing_profile.invoice_notice')}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Form.Item
                  name="carrier_type"
                  label={t('admin.ai_center.billing_profile.carrier_type')}
                >
                  <Select
                    placeholder={t('admin.ai_center.billing_profile.carrier_type_placeholder')}
                    optionLabelProp="label"
                  >
                    {invoiceCapabilities.carrierTypeOptions.map(option => (
                      <Option
                        key={option.value}
                        value={option.value}
                        label={locale === 'zh' ? option.label : option.label_en}
                      >
                        <div style={{ padding: '4px 0' }}>
                          <div style={{ fontWeight: 500 }}>
                            {locale === 'zh' ? option.label : option.label_en}
                          </div>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {locale === 'zh' ? option.description : option.description_en}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Dynamic field rendering — driven entirely by backend invoice_fields */}
                {visibleFields.map(field => (
                  <Form.Item
                    key={field.name}
                    name={field.name}
                    label={locale === 'zh' ? field.label : field.label_en}
                    rules={buildValidationRules(field)}
                    extra={locale === 'zh' ? field.hint : field.hint_en}
                  >
                    <Input
                      placeholder={locale === 'zh' ? field.placeholder : field.placeholder_en}
                      maxLength={field.max_length}
                    />
                  </Form.Item>
                ))}

                {billingProfile?.e_invoice_carrier_display && (
                  <>
                    <Divider />
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label={t('admin.ai_center.billing_profile.current_setting')}>
                        <Text strong>{billingProfile.e_invoice_carrier_display}</Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}
              </Card>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                {t('common.button.save')}
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </div>
    </div>
  );
};

export default BillingProfile;
